import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Modal, Tooltip } from "@mui/material";
import { Info } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";

import Styles from "./Settlement.module.css";
import SettlementRow from "./SettlementRow";
import SettlementFooter from "./SettlementFooter";
import {
  formatINR,
  processConsumerViewData,
  processAdminViewData,
  prepareSubmissionPayload,
} from "./settlementHelpers";

import { ErrorMessage } from "../../../../commons/Toast/ErrorMessage";
import ConfirmationModal from "../../../../commons/ConfirmationModal/ConfirmationModal";
import {
  finalSettlementMakerChecker,
  oneTimeSettlement,
  schedulePayouts,
  updateRepScheduleStatus,
  userFinalSettlement,
  userFinalSettlementGet,
  viewSettlement,
} from "../../../../Services/Schedule/schedule";
import Payouts from "../Payouts/Payouts";
import { formatSlotTime } from "../../../../helpers/dateFormat";
import { walletAccountApi } from "../../../../Services/Payments/PaymentApi";
import { convertDate } from "../../../../commons/FormatDate";
import ViewDoc from "../../ConGen/ViewDoc/ViewDoc";

// ─── Small presentational components ──────────────────────────────────────────

const SummaryCell = ({ label, value }) => (
  <div className={Styles.summaryCell}>
    <span className={Styles.summaryLbl}>{label}</span>
    <span className={Styles.summaryValMono}>{value}</span>
  </div>
);

const SettlementTableHeader = ({ role = "", hideBearerPayer = false }) => {
  const showVerify = role === "MAKER" || role === "CHECKER";

  const columns = [
    { name: "Charge Type", width: "12%" },
    { name: "From User", width: "9%" },
    { name: "To User", width: "9%" },
    { name: "Bearer", width: "6%", show: !hideBearerPayer },
    { name: "Payer", width: "6%", show: !hideBearerPayer },
    {
      name: "Est. Amount (₹)",
      width: "10%",
      tooltip: "Estimated amount as per schedule quantum.",
    },
    {
      name: "Final Amount (₹)",
      width: "10%",
      tooltip: "Final amount as per delivered quantum.",
    },
    { name: "Maker Comment", width: "10%" },
    { name: "Checker Comment", width: "10%" },
    { name: "Verify", width: "6%", show: showVerify },
  ];

  return (
    <div className={`tableHeading display_flex_row ${Styles.rowMainContainer}`}>
      {columns.map((col) => {
        if (col.show === false) return null;
        return (
          <div key={col.name} style={{ width: col.width }}>
            {col.name}
            {col.tooltip && (
              <Tooltip className={Styles.tooltip} title={col.tooltip} arrow>
                <Info className={Styles.infoIcon} />
              </Tooltip>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────

const Settlement = ({
  open,
  handleClose,
  id,
  data,
  handleDeliveryStatus,
  admin,
  renderScheuleData,
  isLast,
  selectedScheduleId,
  paymentScreen = false,
  paymentData,
  heading,
  connection,
  type,
  chargeType,
}) => {

  const role = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole,
  );
  const userId = useSelector(
    (state) => state?.persistedReducer?.user?.user?.userId,
  );
  const userData = useSelector((state) => state?.persistedReducer);
  const isTrader = userData?.roles?.includes("TRADER");

  const [settlementDetails, setSettlementDetails] = useState([]);
  const [activeRowData, setActiveRowData] = useState(null);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [summaryViewDocOpen, setSummaryViewDocOpen] = useState(false);
  const [summaryViewDocConnectionId] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    status: false,
    data: "",
    note: "",
    type: null,
  });

  // ── Derived values ───────────────────────────────────────────────────────────

  const rawDamValue = (() => {
    const price = data?.calculatedDamPrice || 0;
    const rate = data?.energyRate || 0;
    if (data?.energyRateType === "DAM_MINUS")
      return `DAM-${rate}(DAM=${(price + rate).toFixed(3)})`;
    if (data?.energyRateType === "DAM_PLUS")
      return `DAM+${rate}(DAM=${(price - rate).toFixed(3)})`;
    return null;
  })();

  const selfConsumerName =
    userData?.user?.consumers?.find(
      (c) => c.consumerId === data?.selfPartyEntityId,
    )?.consumerName || "-";

  const selfGeneratorName =
    userData?.user?.generators?.find(
      (g) => g.generatorId === data?.selfPartyEntityId,
    )?.generatorName || "-";

  const buyerName =
    role === "GENERATOR"
      ? data?.counterPartyEntityName || "-"
      : data?.selfCompanyName || selfConsumerName;

  const sellerName =
    role === "GENERATOR"
      ? data?.selfCompanyName || selfGeneratorName
      : data?.counterPartyEntityName || "-";

  const fromTime = data?.fromBlock?.timeBlock?.split("-")[0];
  const toTimePart = data?.toBlock?.timeBlock?.split("-")?.[1]?.trim();
  const timeRange =
    data?.type === "ONE_TIME" || !fromTime
      ? "-"
      : `${fromTime} – ${
          toTimePart === "23:59:59" ? "24:00" : formatSlotTime(toTimePart)
        }`;

  const summaryHeaders = [
    { header: "Contract Id", value: data?.bilateralContractUniqueId || "-" },
    // { header: "Buyer → Seller", value: `${buyerName} → ${sellerName}` },
    { header: "Delivery Date", value: convertDate(data?.fromDate) || "-" },
    { header: "Time Range", value: timeRange },
    {
      header: "Scheduled Volume",
      value:
        data?.scheduleVolume != null
          ? `${data.scheduleVolume.toFixed(2)} MWh`
          : "-",
    },
    {
      header: "Delivered Volume",
      value:
        data?.deliveredVolume != null
          ? `${data.deliveredVolume.toFixed(2)} MWh`
          : "-",
    },
  ];

  // ── Data fetching ────────────────────────────────────────────────────────────

  useEffect(() => {
    const fetchData = async () => {
      if (paymentScreen && paymentData?.contractId) {
        await handleOneTimeSettlement();
      } else if (
        paymentScreen ||
        admin ||
        ["MAKER", "CHECKER", "PAYMENT"].includes(data?.status)
      ) {
        await handleAdminGetSettlement();
      } else {
        await handleGetSettlement();
      }
    };
    fetchData();
  }, [id, data, admin, role]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGetSettlement = async () => {
    const res = await viewSettlement({
      id,
      connectionId: data?.bilateralConnectionId,
      isForOneDay: true,
      repScheduleId: data?.id,
    });
    if (res?.data?.code === 200) {
      if (role === "CONSUMER" || role === "TRADER") {
        const processedData = processConsumerViewData(
          res?.data?.data,
          data,
          role,
        ).filter(
          (ele) =>
            ele?.frequency !== "ONE_TIME" ||
            ele?.name === "Extra Margin" ||
            ele?.name === "Energy Charge" ||
            ele?.name === "Refund Request",
        );
        setSettlementDetails(processedData);
      }
    } else {
      ErrorMessage(res?.data?.error);
      handleClose();
    }
  };

  const withInitialMakerFields = (item) => ({
    ...item,
    _initialMakerInputChargeAmount:
      item?.makerInputChargeAmount ?? item?.userInputChargeAmount ?? "",
    isMakerAmountEdited: false,
  });

  const handleAdminGetSettlement = async () => {
    const res = await userFinalSettlementGet({
      scheduleId: data?.scheduleId || paymentData?.schId,
      repScheduleId: selectedScheduleId || data?.id || paymentData?.repId,
    });
    if (res?.data?.code === 200) {
      setSettlementDetails(
        processAdminViewData(res?.data?.data, role).map(withInitialMakerFields),
      );
    }
  };

  const handleOneTimeSettlement = async () => {
    const res = await oneTimeSettlement({
      bCId: data?.bilateralContractId || paymentData?.contractId,
    });
    if (res?.data?.code === 200) {
      setSettlementDetails(
        processAdminViewData(res?.data?.data, role).map(withInitialMakerFields),
      );
    }
  };

  // ── Input handlers ───────────────────────────────────────────────────────────

  const normalizeAmount = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(value);
    return Number.isNaN(num) ? String(value).trim() : num.toFixed(3);
  };

  const handleAmountChange = (e, index) => {
    const val = e.target.value;
    if (!/^\d*\.?\d{0,3}$/.test(val)) return;

    setSettlementDetails((prev) =>
      prev.map((ele, idx) => {
        if (idx !== index) return ele;

        if (role === "MAKER") {
          const initialVal = normalizeAmount(
            ele?._initialMakerInputChargeAmount,
          );
          const currentVal = normalizeAmount(val);
          const isInitialEmpty = initialVal === "" || initialVal === "0.000";
          const edited =
            initialVal !== currentVal || (isInitialEmpty && val !== "");
          const commentStillValid =
            ele?.makerComment?.trim() &&
            normalizeAmount(ele?._commentForAmount) === currentVal;
          return {
            ...ele,
            makerInputChargeAmount: val,
            isMakerAmountEdited: edited,
            ...(commentStillValid
              ? {}
              : { makerComment: "", _commentForAmount: null }),
          };
        }
        if (role === "CHECKER")
          return { ...ele, checkerInputChargeAmount: val };
        return { ...ele, userInputChargeAmount: val };
      }),
    );
  };

  const handleInlineCommentChange = (index, field, value) => {
    setSettlementDetails((prev) =>
      prev.map((ele, idx) => {
        if (idx !== index) return ele;
        if (field === "makerComment") {
          return {
            ...ele,
            makerComment: value,
            _commentForAmount: normalizeAmount(ele?.makerInputChargeAmount),
          };
        }
        return { ...ele, [field]: value };
      }),
    );
  };

  const handleCommentChange = (comment) => {
    setSettlementDetails((prev) =>
      prev.map((ele, idx) => {
        if (idx !== activeRowData?.index) return ele;
        if (role === "MAKER") return { ...ele, makerComment: comment, comment };
        if (role === "CHECKER")
          return { ...ele, checkerComment: comment, comment };
        return { ...ele, comment };
      }),
    );
  };

  // ── Action handlers ──────────────────────────────────────────────────────────

  const handleCheckerAction = (actionType, rowData, index) => {
    setActiveRowData({ ...rowData, index });
    if (actionType === "reject") {
      setConfirmModal({
        status: true,
        type: "reject_row",
        data: "Do you want to reject this value?",
        note: "Once rejected, Maker will need to update the values again.",
      });
    } else if (actionType === "accept") {
      setConfirmModal({
        status: true,
        type: "accept_row",
        data: "Do you want to accept this value?",
        note:
          role === "CHECKER"
            ? "Once accepted, payment will be processed as per the final values and transferred through Bank NOCA."
            : "Once transferred, Checker will verify the amount.",
      });
    }
  };

  const handleFooterAction = (actionType) => {
    if (actionType === "submit") {
      setConfirmModal({
        status: true,
        type: "submit_all",
        data: "Do you want to transfer to maker-checker?",
        note: "After confirmation, the payment will be forwarded to the checker for approval. Upon approval, the amount will be transferred through Bank NOCA.",
      });
    } else if (actionType === "rejectAll") {
      setConfirmModal({
        status: true,
        type: "reject_all",
        data: "Do you want to reject the transaction?",
        note: "It will be transferred back to the maker for verification.",
      });
    } else if (actionType === "transferToMaker") {
      setConfirmModal({
        status: true,
        type: "transfer_maker",
        data: "Do you want to reject this transaction?",
        note: "It will be transferred back to the maker.",
      });
    } else if (actionType === "payout") {
      setPayoutOpen(true);
    }
  };

  // ── Submission ───────────────────────────────────────────────────────────────

  const getWalletId = async ({ role: r, id: wId }) => {
    const res = await walletAccountApi({ role: r, id: wId });
    return res?.data?.code === 200
      ? (res?.data?.data?.walletAccounts?.[0]?.id ?? null)
      : null;
  };

  const submitSettlementData = async ({ isReject = false } = {}) => {
    let nextStatus =
      role === "MAKER" ? "CHECKER" : role === "CHECKER" ? "PAYMENT" : "MAKER";
    if (isReject) nextStatus = "MAKER";

    const statusRes = await updateRepScheduleStatus({
      id: data?.id,
      status: nextStatus,
    });
    if (statusRes?.data?.code !== 200)
      return ErrorMessage(statusRes?.data?.error);

    const [consumerWalletId, generatorWalletId] = await Promise.all([
      getWalletId({
        role: "CONSUMER",
        id: connection?.capacityConsumer?.consumerId,
      }),
      getWalletId({
        role: "GENERATOR",
        id: connection?.capacityGenerator?.generatorId,
      }),
    ]);

    const payload = prepareSubmissionPayload({
      details: settlementDetails,
      role,
      userId,
      scheduleId: data?.scheduleId,
      isReject,
      data,
      selectedScheduleId,
      consumerWalletId,
      generatorWalletId,
    });

    const res =
      role === "MAKER" || role === "CHECKER"
        ? await finalSettlementMakerChecker(payload)
        : await userFinalSettlement(payload);

    if (res?.data?.code === 200) {
      if (role === "CONSUMER") {
        handleDeliveryStatus({
          id: data?.id,
          scheduleId: data?.scheduleId,
          settlement: true,
          payment: true,
        });
      } else {
        renderScheuleData();
        handleAdminGetSettlement();
      }
      setConfirmModal({ status: false });
      handleClose();
    } else {
      ErrorMessage(res?.data?.error);
    }
  };

  const handlePayoutsSubmit = async ({ list }) => {
    const payload = list?.map((ele) => ({
      bilateralContractId: data?.bilateralContractId,
      fromUserId: ele.fromUserId,
      toUserId: ele.toUserId,
      transferAmount: ele.totalAmount,
      receiverType:
        ele?.toUser === ele?.fromUser
          ? "REFUND"
          : ele?.toUser === "Consumer"
            ? "CONSUMER"
            : ele?.toUser === "Generator"
              ? "GENERATOR"
              : "OTHERS",
      fromUser: ele?.fromUser,
      toUser: ele?.toUser,
      status: "PENDING",
      transactionType: ele?.transactionType,
      repScheduleId: ele?.repScheduleId,
      bearer: ele?.bearer,
      payer: ele?.payer,
      creditWalletAccountId: ele?.creditWalletAccountId,
      debitWalletAccountId: ele?.debitWalletAccountId,
    }));
    const res = await schedulePayouts(payload);
    if (res?.data?.code === 200) await submitSettlementData();
  };

  const handleLocalRowUpdate = (updateType) => {
    setSettlementDetails((prev) =>
      prev.map((ele, idx) => {
        if (idx !== activeRowData?.index) return ele;
        if (updateType === "accept_row") {
          return {
            ...ele,
            status: role === "MAKER" ? "CHECKER" : "PAYMENT",
            checkerInputChargeAmount:
              role === "CHECKER"
                ? ele.checkerInputChargeAmount || ele.makerInputChargeAmount
                : null,
            makerInputChargeAmount:
              role === "CHECKER"
                ? ele.checkerInputChargeAmount || ele.makerInputChargeAmount
                : ele.makerInputChargeAmount,
          };
        }
        if (updateType === "reject_row") {
          return {
            ...ele,
            status: "RECHECK",
            checkerInputChargeAmount: null,
            makerInputChargeAmount: null,
          };
        }
        if (updateType === "reset_row") {
          return {
            ...ele,
            status: "MAKER",
            makerInputChargeAmount:
              ele._initialMakerInputChargeAmount ??
              ele.userInputChargeAmount ??
              "",
            isMakerAmountEdited: false,
            makerComment: "",
          };
        }
        return ele;
      }),
    );
    setConfirmModal({ status: false, data: "", note: "", type: null });
  };

  const handleResetRow = (rowData, index) => {
    setActiveRowData({ ...rowData, index });
    setConfirmModal({
      status: true,
      type: "reset_row",
      data: "Reset this row?",
      note: "The verification, final amount, and comment for this charge will be cleared and returned to pending state.",
    });
  };

  const focusCommentInput = (rowIndex, commentType) => {
    setTimeout(() => {
      const input = document.querySelector(
        `input[data-row-index="${rowIndex}"][data-comment-type="${commentType}"]`,
      );
      if (input) {
        input.focus();
        input.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };

  const handleModalSubmit = () => {
    const { type: mType } = confirmModal;

    if (mType === "reject_row" && role === "CHECKER") {
      const activeRow = settlementDetails?.[activeRowData?.index];
      if (!activeRow?.checkerComment?.trim()) {
        setConfirmModal({ status: false, data: "", note: "", type: null });
        focusCommentInput(activeRowData?.index, "checker");
        ErrorMessage(
          "⚠️ Please add a comment explaining why you're rejecting this charge.",
        );
        return;
      }
    }

    if (mType === "accept_row" && role === "MAKER") {
      const activeRow = settlementDetails?.[activeRowData?.index];
      if (activeRow?.isMakerAmountEdited && !activeRow?.makerComment?.trim()) {
        setConfirmModal({ status: false, data: "", note: "", type: null });
        focusCommentInput(activeRowData?.index, "maker");
        ErrorMessage("📝 Please add a comment for your edited value.");
        return;
      }
    }

    if (mType === "submit_all" && role === "MAKER") {
      const firstMissing = settlementDetails
        .filter((ele) => ele?.isMakerAmountEdited === true)
        .find((ele) => !ele?.makerComment?.trim());

      if (firstMissing) {
        const missingIndex = settlementDetails.indexOf(firstMissing);
        setConfirmModal({ status: false, data: "", note: "", type: null });
        focusCommentInput(missingIndex, "maker");
        ErrorMessage(
          "📝 Please add comments for all edited values before submitting.",
        );
        return;
      }
    }

    if (["reject_row", "accept_row", "reset_row"].includes(mType))
      return handleLocalRowUpdate(mType);
    if (mType === "transfer_maker")
      return submitSettlementData({ isReject: true });
    return submitSettlementData();
  };

  // ── Filtered rows ────────────────────────────────────────────────────────────

  const filteredRows = useMemo(
    () =>
      settlementDetails
        .map((ele, originalIndex) => ({ ele, originalIndex }))
        .filter(({ ele }) => {
          if (role === "MAKER" || role === "CHECKER") return true;
          if (!ele?.isView) return false;

          const isTraderCharge =
            ele?.fromUser === "Trader" || ele?.toUser === "Trader";
          const isBuyerCharge =
            ele?.bearer === "BUYER" ||
            ele?.payer === "BUYER" ||
            ele?.fromUserRole === "CONSUMER" ||
            ele?.toUserRole === "CONSUMER";
          const isSellerCharge =
            ele?.bearer === "SELLER" || ele?.payer === "SELLER";

          if (role === "CONSUMER")
            return isTrader ? isBuyerCharge || isTraderCharge : isBuyerCharge;
          if (role === "GENERATOR")
            return isTrader ? isSellerCharge || isTraderCharge : isSellerCharge;
          if (isTrader) return isTraderCharge;

          return true;
        })
        .sort((a, b) => {
          const aGna = (a.ele?.name || a.ele?.chargeName)?.endsWith("_GNA");
          const bGna = (b.ele?.name || b.ele?.chargeName)?.endsWith("_GNA");
          if (aGna === bGna) return 0;
          return aGna ? -1 : 1;
        }),
    [settlementDetails, role, isTrader],
  );
  const hasRecurring = filteredRows.some(
    (r) =>
      r.ele?.frequency === "RECURRING" ||
      r.ele?.chargeFrequency === "RECURRING",
  );

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      {payoutOpen && (
        <Payouts
          open={payoutOpen}
          handleClose={() => setPayoutOpen(false)}
          list={settlementDetails}
          handlePayoutsSubmit={handlePayoutsSubmit}
        />
      )}

      {confirmModal.status && (
        <ConfirmationModal
          open={confirmModal.status}
          data={confirmModal.data}
          note={confirmModal.note || data?.note}
          header="Confirmation"
          handleSubmit={handleModalSubmit}
          handleClose={() => setConfirmModal({ status: false })}
          handleCommentChange={handleCommentChange}
          commentBox={confirmModal.type === "reject_row"}
          commentInit={confirmModal.type === "reject_row"}
        />
      )}

      {summaryViewDocOpen && (
        <ViewDoc
          open={summaryViewDocOpen}
          handleClose={() => setSummaryViewDocOpen(false)}
          connectionId={summaryViewDocConnectionId}
          viewDocData={data}
        />
      )}

      <Modal open={open} onClose={handleClose}>
        <div className={`popupModal ${Styles.modalContent}`}>
          {/* Header */}
          <div className={`modalHeading ${Styles.modalHeader}`}>
            <div className="headingText">Settlement</div>
            <CloseIcon onClick={handleClose} />
          </div>

          {/* Summary strip */}
          <div className={Styles.modalSummaryCell}>
            {summaryHeaders.map((item) => (
              <SummaryCell
                key={item.header}
                label={item.header}
                value={item.value}
              />
            ))}
          </div>

          {/* Table */}
          <div className={Styles.modalTableHeader}>
            <SettlementTableHeader
              role={role}
              hideBearerPayer={hasRecurring}
              showVerify={role === "CHECKER" && type !== "HISTORY"}
            />
          </div>
          <div className={`scroll_bar_y ${Styles.modalTableBody}`}>
            {console.debug("Settlement.jsx - filteredRows:", filteredRows)}
            {filteredRows.map(({ ele, originalIndex }) => (
              <SettlementRow
                key={ele?.id ?? originalIndex}
                ele={ele}
                index={originalIndex}
                role={role}
                onAmountChange={handleAmountChange}
                onCheckerAction={handleCheckerAction}
                onCommentChange={handleInlineCommentChange}
                onResetRow={handleResetRow}
                rawDamValue={rawDamValue}
                type={type}
                hideBearerPayer={hasRecurring}
              />
            ))}
          </div>

          {/* Trader margin summary — visible to trader only */}
          {isTrader && (() => {
            const ecRow = filteredRows.find(
              ({ ele }) => (ele?.chargeName || ele?.name) === "Energy Charge",
            )?.ele;
            const saRow = filteredRows.find(
              ({ ele }) => (ele?.chargeName || ele?.name) === "Seller Amount",
            )?.ele;
            if (!ecRow || !saRow) return null;

            const ecAmt = parseFloat(ecRow?.chargeAmount ?? ecRow?.amount) || 0;
            const saAmt = parseFloat(saRow?.chargeAmount ?? saRow?.amount) || 0;
            const diffAmt = ecAmt - saAmt;

            const ecRate = parseFloat(ecRow?.chargeValue ?? ecRow?.value) || 0;
            const saRate = parseFloat(saRow?.chargeValue ?? saRow?.value) || 0;
            const diffRate = (ecRate - saRate).toFixed(3);

            return (
              <div className={Styles.traderMarginBar}>
                <span className={Styles.traderMarginLabel}>Trader Margin</span>
                <span className={Styles.traderMarginEq}>
                  Energy Charge − Seller Amount
                </span>
                <div className={Styles.traderMarginValues}>
                  <span className={Styles.traderMarginAmt}>
                    ₹&nbsp;{formatINR(diffAmt) ?? "—"}
                  </span>
                  <span className={Styles.traderMarginRate}>
                    ₹{diffRate}/kWh
                  </span>
                </div>
              </div>
            );
          })()}

          {/* Footer */}
          {!paymentScreen && (
            <SettlementFooter
              role={role}
              status={data?.status}
              settlementDetails={filteredRows.map(({ ele }) => ele)}
              onAction={handleFooterAction}
              handleClose={handleClose}
            />
          )}
        </div>
      </Modal>
    </>
  );
};

export default Settlement;
